import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig
} from 'axios';
import type { UserRole } from '@hooddesk/shared-types';
import { useAuthStore } from './auth';

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:4100';
const API_PREFIX = import.meta.env.VITE_API_PREFIX ?? '/v1';

interface RetryConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

export const api: AxiosInstance = axios.create({
  baseURL: `${API_URL}${API_PREFIX}`,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
  timeout: 20_000
});

// ─── request: attach Bearer ───────────────────────────────
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ─── response: 401 → refresh once → retry ─────────────────
let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = useAuthStore.getState().refreshToken;
  if (!refreshToken) return null;

  try {
    const res = await axios.post(
      `${API_URL}${API_PREFIX}/auth/refresh`,
      { refresh_token: refreshToken },
      { withCredentials: true, headers: { 'Content-Type': 'application/json' } }
    );
    const data = res.data as {
      access_token: string;
      refresh_token: string;
      expires_in: number;
      user?: { id: string; society_id: string | null; role: UserRole };
    };
    useAuthStore.getState().setSession({
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
      expiresIn: data.expires_in,
      user: data.user ?? useAuthStore.getState().user
    });
    return data.access_token;
  } catch (e) {
    useAuthStore.getState().clear();
    return null;
  }
}

api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as RetryConfig | undefined;
    if (!original || error.response?.status !== 401 || original._retry) {
      return Promise.reject(error);
    }
    // Don't try to refresh on auth endpoints themselves.
    if (original.url?.includes('/auth/')) {
      return Promise.reject(error);
    }
    original._retry = true;
    if (!refreshPromise) refreshPromise = refreshAccessToken();
    const newToken = await refreshPromise;
    refreshPromise = null;
    if (!newToken) {
      // Hard logout — caller should be redirected by router guard.
      return Promise.reject(error);
    }
    if (original.headers) original.headers.Authorization = `Bearer ${newToken}`;
    return api.request(original);
  }
);

// ─── helpers ──────────────────────────────────────────────
export async function get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const res = await api.get<T>(url, config);
  return res.data;
}
export async function post<T, B = unknown>(url: string, body?: B, config?: AxiosRequestConfig): Promise<T> {
  const res = await api.post<T>(url, body, config);
  return res.data;
}
export async function patch<T, B = unknown>(url: string, body?: B, config?: AxiosRequestConfig): Promise<T> {
  const res = await api.patch<T>(url, body, config);
  return res.data;
}
export async function del<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const res = await api.delete<T>(url, config);
  return res.data;
}

export interface ApiErrorBody {
  error: { code: string; message: string; details?: Record<string, unknown> };
}

export function getErrorMessage(e: unknown): string {
  if (axios.isAxiosError(e)) {
    const body = e.response?.data as ApiErrorBody | undefined;
    if (body?.error?.message) return body.error.message;
    return e.message;
  }
  if (e instanceof Error) return e.message;
  return 'Unknown error';
}
